import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Upload, X } from "lucide-react";
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { supabase } from "../../../lib/supabase";
import { uploadToFtp } from "../../../lib/uploadToFtp";
import UploadProgress from "../../../components/UploadProgress";

const AddEngine: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEditMode = !!id;
  const [loading, setLoading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploading, setUploading] = useState(false);
  
  const [formData, setFormData] = useState({
    title: "",
    description: "",
    price: "",
    oldPrice: "",
    warrantyPeriod: "",
    brand: "HIDEA",
    category: "Outboard",
    imageUrl: "",
  });

  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);

  useEffect(() => {
    if (isEditMode) {
        setLoading(true);
        const fetchEngine = async () => {
            const { data, error } = await supabase.from('engines').select('*').eq('id', id).single();
            if (error) {
                console.error(error);
                toast.error("Motor bilgileri yüklenemedi");
            } else if (data) {
                setFormData({
                    title: data.title,
                    description: data.description,
                    price: data.price,
                    oldPrice: data.oldPrice || "",
                    warrantyPeriod: data.warrantyPeriod || "",
                    brand: data.brand || "HIDEA",
                    category: data.category || "Outboard",
                    imageUrl: data.imageUrl
                });
                if(data.imageUrl) setPreviewUrl(data.imageUrl);
            }
            setLoading(false);
        };
        fetchEngine();
    }
  }, [id, isEditMode]);

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files && event.target.files[0]) {
      const file = event.target.files[0];
      setSelectedFile(file);
      setPreviewUrl(URL.createObjectURL(file));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      let imagePath = formData.imageUrl;

      if (selectedFile) {
        setUploading(true);
        setUploadProgress(0);
        imagePath = await uploadToFtp(selectedFile, "engines", undefined, (p) => setUploadProgress(p));
        setUploading(false);
      }

      if (isEditMode) {
        const { error } = await supabase.from('engines').update({
          ...formData,
          imageUrl: imagePath,
        }).eq('id', id);
        if (error) throw new Error("Motor güncellenemedi");
      } else {
        const { error } = await supabase.from('engines').insert([{
          ...formData,
          imageUrl: imagePath,
        }]);
        if (error) throw new Error("Motor eklenemedi");
      }
      
      toast.success(isEditMode ? "Motor güncellendi" : "Motor başarıyla eklendi");
      navigate("/panel/engines");
      
    } catch (error: any) {
      console.error(error);
      toast.error(error.message || "Bir hata oluştu");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-4xl mx-auto pb-12">
      <div className="flex items-center space-x-4 mb-6">
        <button 
          onClick={() => navigate("/panel/engines")}
          className="p-2 hover:bg-gray-200 rounded-full transition-colors"
        >
          <ArrowLeft className="w-6 h-6" />
        </button>
        <h1 className="text-2xl font-bold text-gray-800">{isEditMode ? "Motor Düzenle" : "Yeni Motor Ekle"}</h1>
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Image Upload */}
          {uploading && <UploadProgress progress={uploadProgress} fileName={selectedFile?.name} />}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Motor Görseli
            </label>
            <div className="mt-1 flex justify-center px-6 pt-5 pb-6 border-2 border-gray-300 border-dashed rounded-lg hover:border-blue-500 transition-colors cursor-pointer relative"
                 onClick={() => document.getElementById('file-upload')?.click()}
            >
              <div className="space-y-1 text-center">
                {previewUrl ? (
                  <div className="relative">
                    <img src={previewUrl} alt="Preview" className="mx-auto h-64 object-cover rounded-md" />
                    <button 
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        setSelectedFile(null);
                        setPreviewUrl(null);
                        // If editing and clearing image, you might want to handle cleaning up formData.imageUrl too, 
                        // but usually keeping the old one until new one is uploaded is safer or clearing it explicitly.
                        // For now, simple clear.
                        setFormData({...formData, imageUrl: ""});
                      }}
                      className="absolute top-2 right-2 p-1 bg-red-500 text-white rounded-full hover:bg-red-600"
                    >
                      <X size={16} />
                    </button>
                  </div>
                ) : (
                  <>
                    <Upload className="mx-auto h-12 w-12 text-gray-400" />
                    <div className="flex text-sm text-gray-600">
                      <span className="relative cursor-pointer bg-white rounded-md font-medium text-blue-600 hover:text-blue-500 focus-within:outline-none focus-within:ring-2 focus-within:ring-offset-2 focus-within:ring-blue-500">
                        Resim Yükle
                      </span>
                    </div>
                    <p className="text-xs text-gray-500">PNG, JPG, GIF up to 10MB</p>
                  </>
                )}
                <input 
                  id="file-upload" 
                  name="file-upload" 
                  type="file" 
                  className="sr-only" 
                  accept="image/*"
                  onChange={handleFileChange}
                />
              </div>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Motor Modeli / Başlık
            </label>
            <input
              type="text"
              required
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 outline-none"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Kategori
            </label>
            <select
              value={formData.category}
              onChange={(e) => setFormData({ ...formData, category: e.target.value })}
              className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 outline-none"
            >
              <option value="Outboard">Outboard</option>
              <option value="Inboard">Inboard</option>
              <option value="İkinci el">İkinci el</option>
              <option value="Elektrikli">Elektrikli</option>
              <option value="Sanal çapa">Sanal çapa</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Marka
            </label>
            <select
              value={formData.brand}
              onChange={(e) => setFormData({ ...formData, brand: e.target.value })}
              className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 outline-none"
            >
               <option value="HIDEA">HIDEA</option>
               <option value="PARSUN">PARSUN</option>
               <option value="TOHATSU">TOHATSU</option>
               <option value="MERCURY">MERCURY</option>
               <option value="Diger">Diğer</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Fiyat
            </label>
            <input
              type="text"
              required
              value={formData.price}
              onChange={(e) => setFormData({ ...formData, price: e.target.value })}
              className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 outline-none"
              placeholder="Örn: 25000 TL"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Eski Fiyat (Opsiyonel)
            </label>
            <input
              type="text"
              value={formData.oldPrice}
              onChange={(e) => setFormData({ ...formData, oldPrice: e.target.value })}
              className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 outline-none"
              placeholder="Örn: 30000 TL (üstü çizili gösterilir)"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Garanti Süresi (Opsiyonel)
            </label>
            <input
              type="text"
              value={formData.warrantyPeriod}
              onChange={(e) => setFormData({ ...formData, warrantyPeriod: e.target.value })}
              className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 outline-none"
              placeholder="Örn: 36 Ay"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Açıklama / Teknik Özellikler
            </label>
            <div className="h-64 mb-12">
                <ReactQuill 
                    theme="snow" 
                    value={formData.description} 
                    onChange={(value: string) => setFormData({...formData, description: value})}
                    className="h-full"
                    modules={{
                        toolbar: [
                            [{ 'header': [1, 2, 3, false] }],
                            ['bold', 'italic', 'underline', 'strike', 'blockquote'],
                            [{'list': 'ordered'}, {'list': 'bullet'}, {'indent': '-1'}, {'indent': '+1'}],
                            [{ 'color': [] }, { 'background': [] }],
                            ['link', 'image'],
                            ['clean']
                        ],
                    }}
                    formats={[
                        'header',
                        'bold', 'italic', 'underline', 'strike', 'blockquote',
                        'list', 'bullet', 'indent',
                        'link', 'image', 'color', 'background'
                    ]}
                />
            </div>
          </div>

          <div className="flex justify-end pt-4">
            <button
              type="submit"
              disabled={loading}
              className="bg-blue-600 hover:bg-blue-700 text-white font-bold py-3 px-8 rounded-lg transition-colors shadow-md disabled:opacity-50"
            >
              {loading ? "Kaydediliyor..." : "Kaydet"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddEngine;
